import * as promise from "bluebird";
import { format as formatTZ } from "date-fns-tz";

import { checkPromiseMapError, handleError } from "../common/ErrorUtil";
// 【T.B.D】マルチパートアップロード
// import { getSliceBlob, uploadThreshold } from "../common/FileUtil";
import { getSliceBlob } from "../common/FileUtil";
import { log, sleep, sleepUnit } from "../model/Common";

import { executeGraphql, executeRest, httpMethod } from "./BaseApi";
import {
  completeMultipartUpload,
  createDownloadZipFilePath,
  createFolder,
  deleteFile,
  startMultipartUpload,
} from "./graphql/mutations";
import {
  getDownloadSignedUrl,
  getUploadSignedUrl,
  listFiles,
} from "./graphql/queries";

/**
 * ファイル一覧取得APIの実行.
 *
 * @param {string} filePrefix ファイルプレフィックス
 * @return {object} レスポンス
 */
export async function executeListFiles(filePrefix) {
  const params = {
    filePrefix,
  };
  const { data, error } = await executeGraphql(listFiles, params).catch(
    handleError
  );

  const key = "listFiles";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      fileInfos: data[key],
    };
  } else {
    response.data = {
      fileInfos: [],
    };
  }

  return response;
}

/**
 * ダウンロード署名付きURL取得APIの実行.
 *
 * @param {string} fileKey ファイルキー
 * @return {object} レスポンス
 */
export async function executeGetDownloadSignedUrl(fileKey) {
  const params = {
    fileKey,
  };
  const { data, error } = await executeGraphql(
    getDownloadSignedUrl,
    params
  ).catch(handleError);

  const key = "getDownloadSignedUrl";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      url: data[key],
    };
  } else {
    response.data = {
      url: "",
    };
  }

  return response;
}

/**
 * ファイルのダウンロード.
 *
 * @param {string} url URL
 * @param {string} fileName ファイル名
 */
export function executeDownload(url, fileName) {
  // aタグ生成
  const a = document.createElement("a");
  // ファイル名を設定
  a.download = fileName;
  a.href = url;
  // ダウンロード
  a.click();
  // aタグ削除
  a.remove();
}

/**
 * ファイルのダウンロード(非同期).
 *
 * @param {string} url URL
 * @param {string} fileName ファイル名
 */
export async function executeDownloadAsync(url, fileName) {
  // aタグ生成
  const a = document.createElement("a");
  // ファイル名を設定
  a.download = fileName;
  a.href = url;
  // ダウンロード
  a.click();
  // aタグ削除
  a.remove();
}

/**
 * ファイルのダウンロード.
 *
 * @param {object} fileInfo ファイル情報
 */
export async function executeDownloadFile(fileInfo) {
  const { fileKey, fileName } = fileInfo;

  // 署名付きURL取得APIの実行
  const { data, error } = await executeGetDownloadSignedUrl(fileKey).catch(
    handleError
  );
  if (error) {
    return { error };
  }

  // ファイルのダウンロードAPIの実行
  executeDownload(data.url, fileName);

  return { data: {} };
}

/**
 * ファイルのダウンロード(S3ポーリングあり).
 *
 * @param {object} fileInfo ファイル情報
 */
async function executeDownloadFileS3Polling(fileInfo, selectedFileKey, label) {
  const { fileKey, fileName } = fileInfo;

  // Lambdaのタイムアウトが15分であるため、S3ポーリングのタイムアウトも15分とする
  const limitTime = sleepUnit.minutes(15);
  const startTime = Date.now();
  let count = 1;

  // 署名付きURL取得APIの実行
  const { data, error } = await executeGetDownloadSignedUrl(fileKey).catch(
    handleError
  );

  if (error) {
    return { error };
  }

  // 署名付きURL取得成功フラグ
  let isSignedUrlSuccess = false;

  log("<==== S3 POLLING START.");
  while (Date.now() - startTime <= limitTime) {
    try {
      log(`======> Try ${count}`);
      count += 1;

      // 署名付きURLのステータスチェック
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(data.url, {
        cache: "no-store",
      });

      if (response.status === 200) {
        log(`======> STATUS CHECK OK. ${fileName}`);
        isSignedUrlSuccess = true;
        break;
      } else {
        // 次のチェック実行前にインターバルを挟む
        // eslint-disable-next-line no-await-in-loop
        await sleep(sleepUnit.seconds(10));
      }
    } catch (e) {
      log.error("Fetch Error:", e);
      handleError({
        code: "SystemError",
      });
      return { error: e };
    }
  }
  log("<==== S3 POLLING END.");

  // 署名付きURL取得成功前にS3ポーリングがタイムアウトした場合
  if (!isSignedUrlSuccess) {
    log(`======> DOWNLOAD FAILED. ${fileName}`);
    const errorObject = {
      code: "S3PollingTimeout",
      fileKey: selectedFileKey,
      label,
    };
    return handleError(errorObject);
  }

  // ダウンロード処理を実行
  await executeDownloadAsync(data.url, fileName);
  log(`======> DOWNLOAD SUCCESS. ${fileName}`);

  return { data: {} };
}

/**
 * アップロード署名付きURL取得APIの実行.
 *
 * @param {object} uploadInfo アップロード情報
 * @param {number} fileSize ファイルサイズ
 * @param {number} chunkIndex Chunkインデックス
 * @return {object} レスポンス
 */
export async function executeGetUploadSignedUrl(
  uploadInfo,
  fileSize,
  chunkIndex
) {
  const params = {
    fileKey: uploadInfo.fileKey,
    fileSize,
    uploadId: uploadInfo.uploadId,
    chunkIndex,
  };
  const { data, error } = await executeGraphql(
    getUploadSignedUrl,
    params
  ).catch(handleError);

  const key = "getUploadSignedUrl";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      url: data[key],
    };
  } else {
    response.data = {
      url: "",
    };
  }

  return response;
}

/**
 * ファイルのアップロード.
 *
 * @param {object} file ファイルオブジェクト
 * @param {string} url アップロードURL
 * @param {object} uploadInfo アップロード情報
 * @param {number} chunkIndex Chunkインデックス
 * @return {object} レスポンス
 */
export async function executeUpload(file, url, uploadInfo, chunkIndex) {
  // Blobを取得
  const blob = getSliceBlob(
    chunkIndex,
    file,
    uploadInfo.chunkSize,
    uploadInfo.chunkNumber
  );
  const headers = {
    "Content-Type": file.type,
  };
  // Blobをアップロード
  const { data, error } = await executeRest(
    httpMethod.put,
    url,
    headers,
    blob
  ).catch(handleError);
  return { data, error };
}

/**
 * マルチパートアップロードの開始APIの実行.
 *
 * @param {string} fileKey ファイルキー
 * @param {number} fileSize ファイルサイズ
 * @param {number} chunkSize Chunkサイズ
 * @return {object} レスポンス
 */
export async function executeStartMultipartUpload(
  fileKey,
  fileSize,
  chunkSize
) {
  const params = {
    input: {
      fileKey,
      fileSize,
      chunkSize,
    },
  };
  const { data, error } = await executeGraphql(
    startMultipartUpload,
    params
  ).catch(handleError);

  const key = "startMultipartUpload";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      uploadInfo: {
        fileKey: data[key].fileKey,
        uploadId: data[key].uploadId,
        chunkNumber: data[key].chunkNumber,
        chunkSize: data[key].chunkSize,
      },
    };
  } else {
    response.data = {
      fileKey: "",
    };
  }
  return response;
}

/**
 * マルチパートアップロードの完了APIの実行.
 *
 * @param {object} uploadInfo アップロード情報
 * @return {object} レスポンス
 */
export async function executeCompleteMultipartUpload(uploadInfo) {
  const params = {
    input: {
      fileKey: uploadInfo.fileKey,
      uploadId: uploadInfo.uploadId,
    },
  };
  const { data, error } = await executeGraphql(
    completeMultipartUpload,
    params
  ).catch(handleError);

  const key = "completeMultipartUpload";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      fileKey: data[key],
    };
  } else {
    response.data = {
      fileKey: "",
    };
  }
  return response;
}

/**
 * ファイルのアップロード
 *
 * @param {object} uploadFile ファイルオブジェクト
 */
export async function executeUploadFile(uploadFile) {
  const { file, filePrefix } = uploadFile;
  const fileKey = filePrefix + file.name;

  let uploadInfo = {};
  // 【T.B.D】マルチパートアップロード
  // HFMAPS AS支援システムの実装検証(PoC)時に構築されたソースであり、実運用では動作していなかった。
  // 実際のマルチパートアップロードとしての動作は未確認状態であり、
  // マルチパートアップロードが必要になった場合はバックエンドLambda(startMultipartUploadや
  // completeMultipartUpload)と併せて、動作確認が必要である。
  // if (uploadThreshold < file.size) {
  //   // マルチパートアップロードの開始APIの実行
  //   const { data, error } = await executeStartMultipartUpload(
  //     fileKey,
  //     file.size,
  //     100 * 1024 * 1024
  //   );
  //   if (error) {
  //     return { error };
  //   }
  //   uploadInfo = data.uploadInfo;
  // } else {
  uploadInfo = {
    fileKey,
    uploadId: null,
    chunkNumber: 1,
    chunkSize: file.size,
  };
  // }

  const indexes = [...Array(uploadInfo.chunkNumber)].map(
    (_, index) => index + 1
  );
  const responses = await promise
    .map(
      indexes,
      async (index) => {
        // 署名付きURL取得APIの実行
        const getUploadSignedUrlResponse = await executeGetUploadSignedUrl(
          uploadInfo,
          file.size,
          index
        ).catch(handleError);
        if (getUploadSignedUrlResponse.error) {
          return { error: getUploadSignedUrlResponse.error };
        }

        // ファイルのアップロード
        const uploadResponse = await executeUpload(
          file,
          getUploadSignedUrlResponse.data.url,
          uploadInfo,
          index
        ).catch(handleError);
        if (uploadResponse.error) {
          return { error: uploadResponse.error };
        }
        return { data: {} };
      },
      {
        concurrency: 1,
      }
    )
    .catch(handleError);

  const promiseMapError = checkPromiseMapError(responses);
  if (promiseMapError) {
    return { error: promiseMapError };
  }

  // 【T.B.D】マルチパートアップロード
  // if (uploadThreshold < file.size) {
  //   // マルチパートアップロードの完了APIの実行
  //   const { error } = await executeCompleteMultipartUpload(uploadInfo).catch(
  //     handleError
  //   );
  //   if (error) {
  //     return { error };
  //   }
  // }

  return { data: {} };
}

/**
 * ファイル削除APIの実行.
 *
 * @param {object} fileInfo ファイル情報
 * @return {object} レスポンス
 */
export async function executeDeleteFile(fileInfo) {
  const params = {
    input: {
      fileKey: fileInfo.fileKey,
    },
  };
  const { data, error } = await executeGraphql(deleteFile, params).catch(
    handleError
  );

  const key = "deleteFile";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      fileKey: data[key],
    };
  } else {
    response.data = {
      fileKey: "",
    };
  }
  return response;
}

/**
 * フォルダ作成APIの実行.
 *
 * @param {string} fileKey ファイルキー
 * @return {object} レスポンス
 */
export async function executeCreateFolder(fileKey) {
  const params = {
    input: {
      fileKey,
    },
  };
  const { data, error } = await executeGraphql(createFolder, params).catch(
    handleError
  );

  const key = "createFolder";
  const response = { error };
  if (data && data[key]) {
    response.data = {
      fileKey: data[key],
    };
  } else {
    response.data = {
      fileKey: "",
    };
  }
  return response;
}

/**
 * ファイルのアップロード(データZIP専用)
 *
 * @param {object} uploadFile ファイルオブジェクト
 */
export async function executeUploadZip(uploadFile) {
  log("==> executeUploadZip: API START.");

  const zipName = uploadFile.file.name;
  let result = false;

  // 実行日時控え
  const nowTime = Date.now();
  const nowDate = formatTZ(new Date(nowTime), "yyyy-MM-dd HH:mm:ss", {
    timeZone: "Asia/Tokyo",
  });

  // ファイルアップロードAPIの実行
  log("====> UPLOAD START.");
  const uploadResponse = await executeUploadFile(uploadFile).catch(handleError);
  if (uploadResponse.error) {
    return { error: uploadResponse.error };
  }
  log("<==== UPLOAD END.");

  // 署名付きURL取得APIの実行(データZIP解析Lambda完了通知ファイル)
  log("====> GET SIGNED-URL.");
  const noticeFileKey = `work/${zipName.split("@")[1]}`;
  const signedUrlResponse = await executeGetDownloadSignedUrl(
    noticeFileKey
  ).catch(handleError);
  if (signedUrlResponse.error) {
    return { error: signedUrlResponse.error };
  }
  log("<==== GET SIGNED-URL.");

  // 通知ファイルに今回のZIPファイル情報が存在するかチェック(指定時間を超過するまで繰り返す)
  log("====> READ WORK-FILES.");
  const limitTime = sleepUnit.minutes(3);
  const startTime = Date.now();
  let count = 1;

  while (Date.now() - startTime <= limitTime) {
    try {
      log(`======> Try ${count}`);
      count += 1;

      // 通知ファイルの内容を取得
      // eslint-disable-next-line no-await-in-loop
      const response = await fetch(signedUrlResponse.data.url, {
        cache: "no-store",
      });

      if (response.status === 200) {
        // 取得成功の場合、データをテキストに変換
        // eslint-disable-next-line no-await-in-loop
        const noticeData = await response.text();

        // 取得データを解析する
        // 1. 今回のZIPファイル名が存在するか
        // 2. 記録されている解析完了日時(Unix Time)はAPI実行日時よりも後か
        log("======> CONVERT DATA.");
        // テキストデータを2次元配列に変換
        const list = noticeData
          .trim()
          .split("\n")
          .map((item) => item.split(","));
        log("list", list);

        // ファイル名が一致する行を取得
        const data = list.find((row) => row[0] === zipName);
        log("data", data);

        if (data) {
          // 解析完了日時とAPI実行日時を比較
          if (Number(data[1]) > nowTime) {
            // 解析完了日時がAPI実行日時よりも後の場合、
            // 今回のアップロードに対する解析結果と判断しチェック終了
            result = true;
            log("<====== CONVERT DATA.");
            break;
          }
        }
        log("<====== CONVERT DATA.");
      }
    } catch (e) {
      // eslint-disable-next-line no-continue
      continue;
    } finally {
      // 次の取得実行前にインターバルを挟む
      // eslint-disable-next-line no-await-in-loop
      await sleep(sleepUnit.seconds(5));
    }
  }
  log("<==== READ WORK-FILES.");

  // 後続のCSV => DB登録Lambdaの処理時間を考慮し、強制インターバルを挟む
  log("====> SLEEP START.");
  await sleep(sleepUnit.seconds(30));
  log("<==== SLEEP END.");

  log("<== executeUploadZip: API END.");
  return {
    data: {
      timestamp: nowDate,
      fileName: zipName,
      result,
    },
  };
}

/**
 * フォルダZIPファイル情報取得API実行&ファイルダウンロードAPI実行.
 *
 * @param {object} fileKey ファイルキー
 * @param {string} label ラベル
 * @return {object} レスポンス
 */
export async function executeCreateDownloadZipFilePath(fileKey, label) {
  const params = {
    input: {
      fileKey,
      label,
    },
  };

  // フォルダZIPファイル情報取得API実行
  const { data, error } = await executeGraphql(
    createDownloadZipFilePath,
    params
  ).catch(handleError);

  const createKey = "createDownloadZipFilePath";

  // Lambda一時領域サイズ超過エラーの場合
  if (data && JSON.stringify(data).includes("LambdaTmpSizeOver")) {
    const errorObject = {
      code: "LambdaTmpSizeOver",
      fileKey,
      label,
    };
    return handleError(errorObject);
  }

  const createDownloadZipFilePathRes = { error };
  if (data && data[createKey]) {
    // レスポンスからフォルダZIPファイル情報を取得、成形
    const zipFileInfo = JSON.parse(data[createKey]);
    createDownloadZipFilePathRes.data = {
      fileKey: zipFileInfo.fileKey,
      fileName: zipFileInfo.fileName,
    };
  } else {
    // レスポンス異常はシステムエラーとして扱う
    return handleError({
      code: "SystemError",
    });
  }

  // ファイルダウンロードAPI実行(S3ポーリングあり)
  const resultRes = await executeDownloadFileS3Polling(
    createDownloadZipFilePathRes.data,
    fileKey,
    label
  );

  return resultRes;
}
